import { useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import ApiUsageModal from './ApiUsageModal';
import { useReduxStore } from '../hooks/useReduxStore';

export default function ApiKeysTable({ keysData = [], onGenerateKey }) {
  const { getState } = useReduxStore();
  const { loading,userData } = getState('AUTH');

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedKey, setSelectedKey] = useState(null);

  const filtered = keysData.filter(
    (d) =>
      d.label?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.key?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d?.user?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d?.user?.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewClick = (key) => {
    setSelectedKey(key);
    setShowModal(true);
  };

  return (
    <div className="min-h-screen p-4 py-8">
      {/* Search Bar and Add Button */}
      <div className="mb-8">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="w-6 h-6 text-white absolute left-4 top-1/2 transform -translate-y-1/2" />
            <input
              type="text"
              placeholder="Search API Keys..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full h-14 pl-12 pr-8 rounded-lg border-2 border-borderGray bg-[#384355] text-white placeholder-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <button
           disabled={true}
            // onClick={onGenerateKey}
            className="bg-primaryBlue text-white px-6 py-3 rounded-lg hover:bg-primaryBlue/90 transition-colors flex items-center gap-2 font-medium whitespace-nowrap cursor-not-allowed"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Add API Key
          </button>
        </div>
      </div>

      {/* Loader */}
      {loading ? (
        <div className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="animate-pulse bg-white border rounded-md p-4">
              <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full"></div>
            </div>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {
            keysData?.length === 0 ? (
            <div className="text-center text-gray-300 text-sm bg-transparent border border-borderGray rounded-md py-12">
              <p className="mb-4 text-lg font-semibold text-white">
                You haven't created any API keys yet.
              </p>
              <p className="mb-6 text-sm text-gray-300">
                Use the "Add API Key" button above to generate your first key and get started with the API integration.
              </p>
            </div>
          ) :
          filtered.length === 0 ? (
            <div className="text-center text-gray-300 text-sm bg-transparent border border-borderGray rounded-md py-12">
              <p className="mb-4 text-lg font-semibold text-white">
               No matching results found.
              </p>
              <p className="mb-6 text-sm text-gray-300">
              Try adjusting your search keywords.
              </p>
            </div>
          ) 
          : (
            <table className="min-w-full text-sm border border-gray-300 bg-transparent rounded-md shadow-sm">
              <thead>
                <tr className="bg-[#384355] text-left text-white text-base font-medium">
                  <th className="py-4 px-3 border-2 border-borderGray">Label</th>
                  <th className="py-4 px-3 border-2 border-borderGray">API Key</th>
                  <th className="py-4 px-3 border-2 border-borderGray">User Name</th>
                  <th className="py-4 px-3 border-2 border-borderGray">Email</th>
                  <th className="py-4 px-3 border-2 border-borderGray">Status</th>
                  <th className="py-4 px-3 border-2 border-borderGray text-center">Usage</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item, idx) => (
                  <tr key={idx} className="bg-transparent hover:bg-gray-50/10">
                    <td className="py-8 px-3 border-2 border-borderGray text-white font-medium">{item.label}</td>
                    <td className="py-8 px-3 border-2 border-borderGray font-mono truncate max-w-[160px] text-gray-300">
                      {item.key}
                    </td>
                    <td className="py-8 px-3 border-2 border-borderGray text-white">{userData.name || '—'}</td>
                    <td className="py-8 px-3 border-2 border-borderGray text-white">{userData?.email || '—'}</td>
                    <td className="py-8 px-3 border-2 border-borderGray">
                      <span
                        className={`text-xs font-semibold px-2 py-1 rounded ${
                          item.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {item.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-8 px-3 border-2 border-borderGray text-center">
                      <button
                        className="bg-primaryBlue text-white px-3 py-1 rounded hover:bg-primaryBlue/80"
                        onClick={() => handleViewClick(item)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Modal */}
      <ApiUsageModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        data={selectedKey}
      />
    </div>
  );
}
